import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { useNotificationContext } from "cerulean";
import ApiHttp from "byzantine/src/ApiHttp";
import { useDomesticWireImportContext } from "./DomesticWireImportContext";
import { WIRE_CATEGORY_KEYS } from "./wireRecipientReviewConstants";
import {
  validateWireField,
  validateWireRow,
} from "./wireRecipientReviewValidation";
import type {
  WireRowEdit,
  WireRowErrors,
  WireImportRow,
  WireImportErrorResponse,
} from "./wireRecipientReviewTypes";

// eslint-disable-next-line import/prefer-default-export
export const useWireRecipientReview = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { sendNotification } = useNotificationContext();
  const { parsedRows, updateParsedRows } = useDomesticWireImportContext();

  // All parsed rows are reviewable (no filtering unlike ACH)
  const newRecipientRows = useMemo(
    () => parsedRows ?? [],
    [parsedRows]
  );

  // --- Editable row state (7 fields) ---
  const [rowEdits, setRowEdits] = useState<Record<number, WireRowEdit>>(
    () => {
      const edits: Record<number, WireRowEdit> = {};
      newRecipientRows.forEach((row, idx) => {
        edits[idx] = {
          recipient_name: row.recipient_name ?? "",
          account_number: row.account_number ?? "",
          routing_number: row.routing_number ?? "",
          street_address: row.recipient_street_address ?? "",
          city: row.recipient_city ?? "",
          region_code: row.recipient_region_code ?? "",
          postal_code: row.recipient_postal_code ?? "",
        };
      });
      return edits;
    }
  );

  // --- Validation errors (merged backend field_errors + client-side) ---
  const [rowErrors, setRowErrors] = useState<
    Record<number, WireRowErrors>
  >(() => {
    const errors: Record<number, WireRowErrors> = {};
    newRecipientRows.forEach((row, idx) => {
      const rowErr: WireRowErrors = {};
      if (row.field_errors) {
        Object.assign(rowErr, row.field_errors);
      }
      const edit = rowEdits[idx];
      if (edit) {
        (
          [
            ["recipient_name", "name"],
            ["account_number", "account_number"],
            ["routing_number", "routing_number"],
            ["street_address", "street_address"],
            ["city", "city"],
            ["region_code", "region_code"],
            ["postal_code", "postal_code"],
          ] as [keyof WireRowEdit, string][]
        ).forEach(([editKey, errorKey]) => {
          if (!rowErr[errorKey]) {
            const error = validateWireField(
              l10n,
              errorKey,
              edit[editKey]
            );
            if (error) {
              rowErr[errorKey] = error;
            }
          }
        });
      }
      if (Object.keys(rowErr).length > 0) {
        errors[idx] = rowErr;
      }
    });
    return errors;
  });

  // --- Track which fields are actively being edited ---
  const [editingFields, setEditingFields] = useState<
    Record<number, Set<string>>
  >({});

  // --- Recipients (from parse step or auto-created) ---
  const [rowRecipients, setRowRecipients] = useState<
    Record<number, API.Recipient>
  >(() => {
    const recipients: Record<number, API.Recipient> = {};
    newRecipientRows.forEach((row, idx) => {
      if (row.recipient) {
        recipients[idx] = row.recipient;
      }
    });
    return recipients;
  });

  // --- Dirty tracking ---
  const [dirtyRows, setDirtyRows] = useState<Set<number>>(new Set());

  // --- Auto-create in-flight tracking ---
  const autoCreatingRef = useRef<Set<number>>(new Set());

  // Selection state — pre-select rows that have hidden recipients (ready to save)
  const [selected, setSelected] = useState<Set<number>>(
    () =>
      new Set(
        newRecipientRows
          .map((row, idx) =>
            row.recipient !== null && row.recipient.hidden ? idx : -1
          )
          .filter((idx) => idx !== -1)
      )
  );

  // Pre-mark rows with non-hidden recipients as already saved
  const [saved, setSaved] = useState<Set<number>>(
    () =>
      new Set(
        newRecipientRows
          .map((row, idx) =>
            row.recipient !== null && !row.recipient.hidden ? idx : -1
          )
          .filter((idx) => idx !== -1)
      )
  );
  const [saving, setSaving] = useState<Set<number>>(new Set());
  const [nextLoading, setNextLoading] = useState(false);

  // --- Derived state ---
  const hasAnyErrors = Object.values(rowErrors).some(
    (errs) => Object.keys(errs).length > 0
  );

  const errorCategories = useMemo(() => {
    const categories = new Set<string>();
    const requiredMsg = l10n.getString(
      "wire-recipient-review-validation-required"
    );
    Object.values(rowErrors).forEach((errs) => {
      Object.entries(errs).forEach(([key, msg]) => {
        if (!WIRE_CATEGORY_KEYS.includes(key)) return;
        if (msg === requiredMsg) {
          categories.add("required");
        } else {
          categories.add(key);
        }
      });
    });
    return Array.from(categories);
  }, [rowErrors, l10n]);

  const unsavedIdxs = newRecipientRows
    .map((_, idx) => idx)
    .filter(
      (idx) => rowRecipients[idx] !== undefined && !saved.has(idx)
    );

  const allSelected =
    unsavedIdxs.length > 0 &&
    unsavedIdxs.every((idx) => selected.has(idx));
  const someSelected = selected.size > 0 && !allSelected;

  // --- Build wire import payload row from edit state ---
  const buildImportRow = (
    edit: WireRowEdit,
    uuid?: string
  ): WireImportRow => {
    const row: WireImportRow = {
      name: edit.recipient_name.trim(),
      routing_number: edit.routing_number.trim(),
      account_number: edit.account_number.trim(),
      street_address: edit.street_address.trim(),
      city: edit.city.trim(),
      region_code: edit.region_code.trim(),
      postal_code: edit.postal_code.trim(),
    };
    if (uuid) {
      row.uuid = uuid;
    }
    return row;
  };

  // --- Auto-create hidden recipient via wire_import_recipients ---
  const autoCreateRecipient = useCallback(
    async (idx: number, edit: WireRowEdit) => {
      if (autoCreatingRef.current.has(idx)) return;
      autoCreatingRef.current.add(idx);

      try {
        const payload = { rows: [buildImportRow(edit)] };
        const response = await ApiHttp.fetch(
          "recipients/wire_import_recipients/",
          { method: "POST" },
          payload
        );
        const results = response as
          | { results: API.Recipient[] }
          | API.Recipient[];
        const created = Array.isArray(results)
          ? results[0]
          : results.results[0];
        setRowRecipients((prev) => ({ ...prev, [idx]: created }));
        setSelected((prev) => new Set(prev).add(idx));

        // Clear any stale backend errors for this row now that creation succeeded
        setRowErrors((prev) => {
          const next = { ...prev };
          delete next[idx];
          return next;
        });

        // Sync back to context
        updateParsedRows((rows) =>
          rows.map((r, i) =>
            i === idx ? { ...r, recipient: created } : r
          )
        );
      } catch (error) {
        if (error && typeof error === "object" && "errors" in error) {
          const wireError = error as WireImportErrorResponse;
          setRowErrors((prev) => {
            const next = { ...prev };
            wireError.errors.forEach((rowErr) => {
              if (rowErr.index === 0) {
                next[idx] = { ...next[idx], ...rowErr.errors };
              }
            });
            return next;
          });
        }
        sendNotification({
          type: "negative",
          text: l10n.getString("recipient-review-bulk-update-error"),
        });
      } finally {
        autoCreatingRef.current.delete(idx);
      }
    },
    [l10n, sendNotification, updateParsedRows]
  );

  // --- Field editing handlers ---
  const startEditing = (idx: number, field: string) => {
    setEditingFields((prev) => {
      const fieldSet = new Set(prev[idx] ?? []);
      fieldSet.add(field);
      return { ...prev, [idx]: fieldSet };
    });
  };

  const isEditing = (idx: number, field: string): boolean => {
    const errors = rowErrors[idx] ?? {};
    if (errors[field]) return true;
    return editingFields[idx]?.has(field) ?? false;
  };

  const handleFieldChange = (
    idx: number,
    field: keyof WireRowEdit,
    value: string
  ) => {
    setRowEdits((prev) => ({
      ...prev,
      [idx]: { ...prev[idx], [field]: value },
    }));
  };

  const handleFieldBlur = (idx: number, field: keyof WireRowEdit) => {
    const edit = rowEdits[idx];
    if (!edit) return;

    const fieldKey = field === "recipient_name" ? "name" : field;
    const error = validateWireField(l10n, fieldKey, edit[field]);

    setRowErrors((prev) => {
      const rowErr = { ...prev[idx] };
      if (error) {
        rowErr[fieldKey] = error;
      } else {
        delete rowErr[fieldKey];
      }
      return { ...prev, [idx]: rowErr };
    });

    if (!error && edit[field].trim()) {
      setEditingFields((prev) => {
        const fieldSet = new Set(prev[idx] ?? []);
        fieldSet.delete(fieldKey);
        return { ...prev, [idx]: fieldSet };
      });
    }

    if (rowRecipients[idx]) {
      setDirtyRows((prev) => new Set(prev).add(idx));
    }

    const fullErrors = validateWireRow(l10n, edit);
    if (
      Object.keys(fullErrors).length === 0 &&
      !rowRecipients[idx]
    ) {
      autoCreateRecipient(idx, edit);
    }
  };

  // --- Region dropdown handler (constrained to STATES list) ---
  const handleRegionChange = (idx: number, value: string) => {
    setRowEdits((prev) => ({
      ...prev,
      [idx]: { ...prev[idx], region_code: value },
    }));

    // Value is always valid from the dropdown; clear any existing error
    setRowErrors((prev) => {
      const rowErr = { ...prev[idx] };
      delete rowErr.region_code;
      return { ...prev, [idx]: rowErr };
    });

    // Mark dirty if recipient already exists
    if (rowRecipients[idx]) {
      setDirtyRows((prev) => new Set(prev).add(idx));
    }

    // Trigger auto-create if row is now fully valid and has no recipient
    const updatedEdit = { ...rowEdits[idx], region_code: value };
    const fullErrors = validateWireRow(l10n, updatedEdit);
    if (
      Object.keys(fullErrors).length === 0 &&
      !rowRecipients[idx]
    ) {
      autoCreateRecipient(idx, updatedEdit);
    }
  };

  // --- Select/deselect handlers ---
  const handleToggleSelectAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(unsavedIdxs));
    }
  };

  const handleToggleRow = (idx: number) => {
    if (saved.has(idx)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  // --- Save (un-hide) a single recipient ---
  // Always syncs current wire details then un-hides (no dirty guard —
  // ensures edits are always persisted before the recipient becomes visible)
  // 2 step process because fedwire_destination is read-only on recipient
  const handleSaveRecipient = useCallback(
    async (idx: number) => {
      const recipient = rowRecipients[idx];
      if (!recipient || saved.has(idx) || saving.has(idx)) return;

      const edit = rowEdits[idx];
      const errors = edit ? validateWireRow(l10n, edit) : {};
      if (Object.keys(errors).length > 0) return;

      setSaving((prev) => new Set(prev).add(idx));
      try {
        // Step 1: Sync current wire details via wire_import_recipients
        await ApiHttp.fetch(
          "recipients/wire_import_recipients/",
          { method: "POST" },
          { rows: [buildImportRow(edit, recipient.id)] }
        );
        setDirtyRows((prev) => {
          const next = new Set(prev);
          next.delete(idx);
          return next;
        });

        // Step 2: Un-hide the recipient
        await ApiHttp.fetch(
          `recipients/${recipient.id}/`,
          { method: "PUT" },
          {
            name: edit.recipient_name.trim(),
            type: "business",
            hidden: false,
          }
        );
        setSaved((prev) => new Set(prev).add(idx));
        setSelected((prev) => {
          const next = new Set(prev);
          next.delete(idx);
          return next;
        });
      } catch (error) {
        let errorText = l10n.getString("recipient-review-save-error");
        if (error && typeof error === "object" && "detail" in error) {
          errorText = (error as { detail: string }).detail;
        }
        sendNotification({
          type: "negative",
          text: errorText,
        });
      } finally {
        setSaving((prev) => {
          const next = new Set(prev);
          next.delete(idx);
          return next;
        });
      }
    },
    [rowRecipients, rowEdits, saved, saving, l10n, sendNotification]
  );

  const handleSaveAll = useCallback(async () => {
    const unsavedSelected = Array.from(selected).filter(
      (idx) => !saved.has(idx) && !saving.has(idx)
    );
    await Promise.allSettled(
      unsavedSelected.map((idx) => handleSaveRecipient(idx))
    );
  }, [selected, saved, saving, handleSaveRecipient]);

  const handleClearSelection = () => {
    setSelected(new Set());
  };

  const handleBack = () => {
    navigate("../map-columns");
  };

  // --- Next: update dirty rows via wire_import_recipients, then navigate ---
  const handleNext = useCallback(async () => {
    if (hasAnyErrors) return;

    const dirtyRowsArray = Array.from(dirtyRows).filter(
      (idx) => rowRecipients[idx]
    );

    if (dirtyRowsArray.length === 0) {
      navigate("../payment-details");
      return;
    }

    setNextLoading(true);
    try {
      const payload = {
        rows: dirtyRowsArray.map((idx) => {
          const edit = rowEdits[idx];
          return buildImportRow(edit, rowRecipients[idx].id);
        }),
      };
      await ApiHttp.fetch(
        "recipients/wire_import_recipients/",
        { method: "POST" },
        payload
      );

      // Sync updated recipients back to context
      updateParsedRows((rows) => {
        const updated = [...rows];
        dirtyRowsArray.forEach((idx) => {
          const edit = rowEdits[idx];
          const existing = rowRecipients[idx];
          updated[idx] = {
            ...updated[idx],
            recipient: {
              ...existing,
              name: edit.recipient_name.trim(),
            },
          };
        });
        return updated;
      });

      setDirtyRows(new Set());
      navigate("../payment-details");
    } catch (error) {
      if (error && typeof error === "object" && "errors" in error) {
        const wireError = error as WireImportErrorResponse;
        const dirtyArr = Array.from(dirtyRows).filter(
          (idx) => rowRecipients[idx]
        );
        setRowErrors((prev) => {
          const next = { ...prev };
          wireError.errors.forEach((rowErr) => {
            const realIdx = dirtyArr[rowErr.index];
            if (realIdx !== undefined) {
              next[realIdx] = {
                ...next[realIdx],
                ...rowErr.errors,
              };
            }
          });
          return next;
        });
      }
      sendNotification({
        type: "negative",
        text: l10n.getString("recipient-review-bulk-update-error"),
      });
    } finally {
      setNextLoading(false);
    }
  }, [
    hasAnyErrors,
    dirtyRows,
    rowRecipients,
    rowEdits,
    updateParsedRows,
    navigate,
    l10n,
    sendNotification,
  ]);

  // --- Tag helpers ---
  const getTagClassName = (
    idx: number,
    tagStyles: Record<string, string>
  ) => {
    if (saved.has(idx)) return tagStyles.saveTagSaved;
    if (saving.has(idx)) return tagStyles.saveTagSaving;
    return tagStyles.saveTag;
  };

  const getTagLabel = (idx: number) => {
    if (saved.has(idx)) {
      return l10n.getString(
        "recipient-review-added-to-recipients"
      );
    }
    return l10n.getString("recipient-review-add-to-recipients");
  };

  const canSaveRow = (idx: number): boolean => {
    const errors = rowErrors[idx];
    const hasErrors = errors && Object.keys(errors).length > 0;
    return (
      !hasErrors &&
      rowRecipients[idx] !== undefined &&
      !saved.has(idx) &&
      !saving.has(idx)
    );
  };

  return {
    l10n,
    parsedRows,
    newRecipientRows,
    rowEdits,
    rowErrors,
    rowRecipients,
    selected,
    saved,
    saving,
    nextLoading,
    hasAnyErrors,
    errorCategories,
    allSelected,
    someSelected,
    startEditing,
    isEditing,
    handleFieldChange,
    handleFieldBlur,
    handleRegionChange,
    handleToggleSelectAll,
    handleToggleRow,
    handleSaveRecipient,
    handleSaveAll,
    handleClearSelection,
    handleBack,
    handleNext,
    getTagClassName,
    getTagLabel,
    canSaveRow,
  };
};
